import { v4 as uuidv4 } from 'uuid';
import { UploadImage, UploadVideoChunked } from '../../util/cloudinary';
import { storableError } from '../../util/errors';
import { updateProfile as updateProfileAction } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { buildStoreProfileSeoTags, buildStoreBannerSeoTags, buildStoreVideoSeoTags } from '../../util/imageSeoTags';
import { updateCloudinaryContext } from '../../util/api';

// ================ Action types ================ //
export const UPDATE_STORE_BASIC_INFO_REQUEST =
    'app/StoreBasicInformationPage/UPDATE_STORE_BASIC_INFO_REQUEST';
export const UPDATE_STORE_BASIC_INFO_SUCCESS =
    'app/StoreBasicInformationPage/UPDATE_STORE_BASIC_INFO_SUCCESS';
export const UPDATE_STORE_BASIC_INFO_ERROR =
    'app/StoreBasicInformationPage/UPDATE_STORE_BASIC_INFO_ERROR';
export const SET_VIDEO_UPLOAD_PROGRESS =
    'app/StoreBasicInformationPage/SET_VIDEO_UPLOAD_PROGRESS';
export const CLEAR_VIDEO_UPLOAD_PROGRESS =
    'app/StoreBasicInformationPage/CLEAR_VIDEO_UPLOAD_PROGRESS';
export const SET_VIDEO_UPLOAD_COMPLETE =
    'app/StoreBasicInformationPage/SET_VIDEO_UPLOAD_COMPLETE';
export const SET_VIDEO_UPLOAD_ERROR =
    'app/StoreBasicInformationPage/SET_VIDEO_UPLOAD_ERROR';
export const CLEAR_VIDEO_UPLOAD_STATE =
    'app/StoreBasicInformationPage/CLEAR_VIDEO_UPLOAD_STATE';
export const SET_PHOTO_UPLOAD_PROGRESS =
    'app/StoreBasicInformationPage/SET_PHOTO_UPLOAD_PROGRESS';
export const SET_PHOTO_UPLOAD_COMPLETE =
    'app/StoreBasicInformationPage/SET_PHOTO_UPLOAD_COMPLETE';
export const SET_PHOTO_UPLOAD_ERROR =
    'app/StoreBasicInformationPage/SET_PHOTO_UPLOAD_ERROR';
export const CLEAR_PHOTO_UPLOAD_STATE =
    'app/StoreBasicInformationPage/CLEAR_PHOTO_UPLOAD_STATE';

// ================ Reducer ================ //
const initialState = {
    updateInProgress: false,
    updateError: null,
    videoUploadProgress: 0,
    videoUploadInProgress: false,
    videoUploadData: null,
    videoUploadError: null,
    photoUploadProgress: 0,
    photoUploadInProgress: false,
    photoUploadData: null,
    photoUploadError: null,
};

export default function reducer(
    state = initialState,
    action = {}
) {
    const { type, payload } = action;
    switch (type) {
        case UPDATE_STORE_BASIC_INFO_REQUEST:
            return { ...state, updateInProgress: true, updateError: null };
        case UPDATE_STORE_BASIC_INFO_SUCCESS:
            return { ...state, updateInProgress: false, updateError: null };
        case UPDATE_STORE_BASIC_INFO_ERROR:
            return { ...state, updateInProgress: false, updateError: payload };
        case SET_VIDEO_UPLOAD_PROGRESS:
            return {
                ...state,
                videoUploadProgress: payload.progress,
                videoUploadInProgress: payload.progress >= 0 && payload.progress < 100,
            };
        case CLEAR_VIDEO_UPLOAD_PROGRESS:
            return {
                ...state,
                videoUploadProgress: 0,
                videoUploadInProgress: false,
            };
        case SET_VIDEO_UPLOAD_COMPLETE:
            return {
                ...state,
                videoUploadProgress: 100,
                videoUploadInProgress: false,
                videoUploadData: payload.uploadData,
                videoUploadError: null,
            };
        case SET_VIDEO_UPLOAD_ERROR:
            return {
                ...state,
                videoUploadProgress: 0,
                videoUploadInProgress: false,
                videoUploadError: payload.error,
            };
        case CLEAR_VIDEO_UPLOAD_STATE:
            return {
                ...state,
                videoUploadProgress: 0,
                videoUploadInProgress: false,
                videoUploadData: null,
                videoUploadError: null,
            };
        case SET_PHOTO_UPLOAD_PROGRESS:
            return {
                ...state,
                photoUploadProgress: payload.progress,
                photoUploadInProgress: payload.progress > 0 && payload.progress < 100,
                photoUploadError: null,
            };
        case SET_PHOTO_UPLOAD_COMPLETE:
            return {
                ...state,
                photoUploadProgress: 100,
                photoUploadInProgress: false,
                photoUploadData: payload.uploadData,
                photoUploadError: null,
            };
        case SET_PHOTO_UPLOAD_ERROR:
            return {
                ...state,
                photoUploadProgress: 0,
                photoUploadInProgress: false,
                photoUploadError: payload.error,
            };
        case CLEAR_PHOTO_UPLOAD_STATE:
            return {
                ...state,
                photoUploadProgress: 0,
                photoUploadInProgress: false,
                photoUploadData: null,
                photoUploadError: null,
            };
        default:
            return state;
    }
}

// ================ Action creators ================ //
export const updateStoreBasicInfoRequest = () => ({ type: UPDATE_STORE_BASIC_INFO_REQUEST });
export const updateStoreBasicInfoSuccess = () => ({
    type: UPDATE_STORE_BASIC_INFO_SUCCESS,
});
export const updateStoreBasicInfoError = error => ({
    type: UPDATE_STORE_BASIC_INFO_ERROR,
    error: true,
    payload: error,
});

export const setVideoUploadProgress = progress => ({
    type: SET_VIDEO_UPLOAD_PROGRESS,
    payload: { progress },
});

export const clearVideoUploadProgress = () => ({
    type: CLEAR_VIDEO_UPLOAD_PROGRESS,
});

export const setVideoUploadComplete = uploadData => ({
    type: SET_VIDEO_UPLOAD_COMPLETE,
    payload: { uploadData },
});

export const setVideoUploadError = error => ({
    type: SET_VIDEO_UPLOAD_ERROR,
    error: true,
    payload: { error },
});

export const clearVideoUploadState = () => ({
    type: CLEAR_VIDEO_UPLOAD_STATE,
});

export const setPhotoUploadProgress = progress => ({
    type: SET_PHOTO_UPLOAD_PROGRESS,
    payload: { progress },
});

export const setPhotoUploadComplete = uploadData => ({
    type: SET_PHOTO_UPLOAD_COMPLETE,
    payload: { uploadData },
});

export const setPhotoUploadError = error => ({
    type: SET_PHOTO_UPLOAD_ERROR,
    error: true,
    payload: { error },
});

export const clearPhotoUploadState = () => ({
    type: CLEAR_PHOTO_UPLOAD_STATE,
});

// ================ Selectors ================ //
export const getUpdateInProgress = state =>
    state.StoreBasicInformationPage?.updateInProgress || false;

export const getUpdateError = state =>
    state.StoreBasicInformationPage?.updateError;

export const getVideoUploadProgress = state =>
    state.StoreBasicInformationPage?.videoUploadProgress || 0;

export const getVideoUploadInProgress = state =>
    state.StoreBasicInformationPage?.videoUploadInProgress || false;

export const getVideoUploadData = state =>
    state.StoreBasicInformationPage?.videoUploadData || null;

export const getVideoUploadError = state =>
    state.StoreBasicInformationPage?.videoUploadError || null;

export const getPhotoUploadProgress = state =>
    state.StoreBasicInformationPage?.photoUploadProgress || 0;

export const getPhotoUploadInProgress = state =>
    state.StoreBasicInformationPage?.photoUploadInProgress || false;

export const getPhotoUploadData = state =>
    state.StoreBasicInformationPage?.photoUploadData || null;

export const getPhotoUploadError = state =>
    state.StoreBasicInformationPage?.photoUploadError || null;

// ================ Thunks ================ //

// loadData is a collection of async calls that need to be made
// before page has all the info it needs to render itself
export const loadData = (params, search, config) => (dispatch, getState) => {
    // No additional data loading required for this page
    return Promise.resolve();
};

// Upload display photo immediately when file is selected
export const uploadDisplayPhoto = (file, photoType = 'profile') => {
    return async (dispatch) => {
        try {
            const photoId = uuidv4();


            // Simulate progress for better UX (images upload quickly, so show activity)
            // Set to 30% to indicate upload in progress
            dispatch(setPhotoUploadProgress(30));

            // Upload image to Cloudinary
            const uploadResult = await UploadImage({
                file,
                id: photoId,
                folder: 'store owners'
            });

            // Prepare photo data
            const photoData = {
                public_id: uploadResult.public_id,
                secure_url: uploadResult.secure_url,
                photoType,
            };

            // Mark upload as complete
            dispatch(setPhotoUploadComplete(photoData));

            return photoData;
        } catch (error) {
            console.error('Photo upload failed:', error);
            const errorMessage = error.message || 'Failed to upload photo';
            dispatch(setPhotoUploadError(errorMessage));
            throw error;
        }
    };
};

// Upload display video immediately when file is selected
export const uploadDisplayVideo = (file) => {
    return async (dispatch) => {
        try {
            const videoId = uuidv4();

            // Start upload progress
            dispatch(setVideoUploadProgress(1));

            // Upload video to Cloudinary (chunked for large files)
            const uploadResult = await UploadVideoChunked({
                file,
                id: videoId,
                folder: 'store owners',
                onProgress: (progress) => {
                    dispatch(setVideoUploadProgress(progress));
                },
            });

            // Prepare video data
            const videoData = {
                public_id: uploadResult.public_id,
                playback_url: uploadResult.playback_url,
                width: uploadResult.width,
                height: uploadResult.height,
            };

            // Mark upload as complete
            dispatch(setVideoUploadComplete(videoData));

            return videoData;
        } catch (error) {
            console.error('Video upload failed:', error);
            const errorMessage = error.message || 'Failed to upload video';
            dispatch(setVideoUploadError(errorMessage));
            // Clear progress on error
            dispatch(setVideoUploadProgress(0));
            throw error;
        }
    };
};

// Update Store Basic Information - handles image/video uploads and profile update
export const updateStoreBasicInfo = (formValues, personalAddress, currentUser) => {
    return async (dispatch, getState, sdk) => {
        dispatch(updateStoreBasicInfoRequest());

        try {
            const {
                storeDisplayPhoto,
                storeName,
                seoTitle,
                seoDescription,
                storeAbout,
                sameAsPersonalAddress,
                storeAddress: storeAddressValue,
                storeCity,
                storeState,
                storePostcode,
                storeCountry,
                videoUrl,
                video,
                videoTitle,
                videoDescription,
            } = formValues;


            // Check if photo/video was already uploaded (immediate upload)
            const state = getState();
            const uploadedPhoto = getPhotoUploadData(state);
            const uploadedVideo = getVideoUploadData(state);

            // Helper function to get display photo data
            const getDisplayPhotoData = async () => {
                // Use already uploaded photo if available
                if (uploadedPhoto) {
                    return uploadedPhoto;
                }

                // Keep existing photo if no new upload
                if (storeDisplayPhoto?.public_id) {
                    return {
                        public_id: storeDisplayPhoto.public_id,
                        secure_url: storeDisplayPhoto.secure_url,
                    };
                }
                return null;
            };

            // Helper function to get video data
            const getVideoData = async () => {
                // Use already uploaded video if available
                if (uploadedVideo) {
                    return uploadedVideo;
                }

                // Use video URL if provided
                if (videoUrl) {
                    return { videoUrl };
                }

                // Keep existing video if no new upload
                if (video?.public_id) {
                    return {
                        public_id: video.public_id,
                        playback_url: video.playback_url,
                    };
                }
                return null;
            };

            // Get image and video data (already uploaded, so no need to upload again)
            const [displayPhotoData, videoData] = await Promise.all([
                getDisplayPhotoData(),
                getVideoData(),
            ]);

            // Resolve city for SEO tag generation (selectedPlace takes precedence)
            const resolvedCity = storeAddressValue?.selectedPlace?.city || storeCity || '';

            // Generate SEO tags for the uploaded photo and push them to Cloudinary (fire-and-forget)
            if (displayPhotoData?.public_id) {
                const seoTags = displayPhotoData.photoType === 'banner'
                    ? buildStoreBannerSeoTags(storeName, resolvedCity)
                    : buildStoreProfileSeoTags(storeName, resolvedCity);
                displayPhotoData.seoTags = seoTags;
                updateCloudinaryContext({
                    publicId: displayPhotoData.public_id,
                    context: seoTags,
                    resourceType: 'image',
                }).catch(err => console.error('Cloudinary context update failed:', err));
            }

            // Generate SEO tags for the uploaded video and push them to Cloudinary (fire-and-forget)
            if (videoData?.public_id) {
                const videoSeoTags = buildStoreVideoSeoTags(
                    storeName,
                    resolvedCity,
                    videoTitle || '',
                    videoDescription || ''
                );
                videoData.seoTags = videoSeoTags;
                updateCloudinaryContext({
                    publicId: videoData.public_id,
                    context: videoSeoTags,
                    resourceType: 'video',
                }).catch(err => console.error('Cloudinary video context update failed:', err));
            }

            // Prepare address data
            let addressData = null;
            if (storeAddressValue?.selectedPlace) {
                // Use store address from selectedPlace or individual fields
                const selectedPlace = storeAddressValue?.selectedPlace;
                addressData = {
                    address: selectedPlace?.address || '',
                    city: selectedPlace?.city || storeCity || '',
                    state: selectedPlace?.state || storeState || '',
                    postcode: selectedPlace?.postcode || storePostcode || '',
                    country: selectedPlace?.country || storeCountry || '',
                    origin: selectedPlace?.origin || null,
                };
            }

            // If new video was saved, delete old saved video if it exists and is different
            if (videoData?.public_id) {
                const currentUserState = getState();
                const currentUserData = currentUserState.user.currentUser;
                const oldVideo = currentUserData?.attributes?.profile?.publicData?.storeVideo;

                // Delete old video if it exists and is different from new one
                if (oldVideo?.public_id && oldVideo.public_id !== videoData.public_id) {
                    try {
                        const { DeleteMedia } = await import('../../util/cloudinary');
                        await DeleteMedia({
                            publicId: oldVideo.public_id,
                            resourceType: 'video'
                        });
                    } catch (error) {
                        console.error('Failed to delete old saved video:', error);
                        // Continue anyway - orphaned file is acceptable
                    }
                }
            }

            // Prepare publicData payload
            const publicDataPayload = {
                ...(displayPhotoData && { storeDisplayPhoto: displayPhotoData }),
                ...(storeName && { storeName: storeName }),
                ...(seoTitle && { seoTitle: seoTitle }),
                ...(seoDescription && { seoDescription: seoDescription }),
                ...(storeAbout && { storeAbout: storeAbout }),
                storeAddressSameAsPersonal: sameAsPersonalAddress,
                ...(addressData && { storeAddress: addressData }),
                ...(videoData && { storeVideo: videoData }),
                ...(videoTitle && { storeVideoTitle: videoTitle }),
                ...(videoDescription && { storeVideoDescription: videoDescription }),
            };

            const payload = {
                publicData: publicDataPayload,
            };

            // Call updateProfile thunk from ProfileSettingsPage
            await dispatch(updateProfileAction(payload));

            dispatch(updateStoreBasicInfoSuccess());
            // Clear upload progress and states on success
            dispatch(clearVideoUploadProgress());
            dispatch(clearVideoUploadState());
            dispatch(clearPhotoUploadState());
            return true;
        } catch (error) {
            console.error('Error updating store basic information:', error);
            const storableErr = storableError(error);
            dispatch(updateStoreBasicInfoError(storableErr));
            // Clear upload progress on error (but keep uploaded data for retry)
            dispatch(clearVideoUploadProgress());
            // Note: We don't clear photo/video upload state on error so user can retry save
            return false;
        }
    };
};

